import React, { useState, useEffect } from "react";
import { Button, Offcanvas, Form } from "react-bootstrap";
import { MdAttachFile } from "react-icons/md";
import { FaLink } from "react-icons/fa";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import * as apicall from "../Apicall";
import Skeleton from "../Loader/Skeleton";

// Shop images can come back either as an array or as the raw comma separated
// column value, so normalise both into a plain list of URLs.
const toImageList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value || value === "null") return [];
  return String(value)
    .split(",")
    .map((img) => img.trim())
    .filter(Boolean);
};

// How many shop photos a vendor may keep, and the per-file size cap.
const MAX_OFFICE_IMAGES = 10;
const MAX_IMAGE_SIZE = 200 * 1024;

const BusinessInfoSidebar = ({ show, onHide, vendorData }) => {
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    tax: "",
    taxNo: "",
    invoicePrefix: "",
    website_link: "",
    notes: "",
    email: ""
  });

  const [newImage, setNewImage] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeCollab, setRemoveCollab] = useState(false);
  const [officeImages, setOfficeImages] = useState([]);
  const [initialOfficeImages, setInitialOfficeImages] = useState([]);

  const businessSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.(com|co\.in)$/,
        "Only .com or .co.in emails are allowed"
      ),
    // Booking invoice numbers read <prefix>-<year>-<number>; blank = INV.
    invoicePrefix: Yup.string().matches(
      /^[A-Za-z0-9]{0,10}$/,
      "Use up to 10 letters or digits"
    )
  });

  useEffect(() => {
    if (!show) return;
    if (!vendorData) return;

    setFormData({
      tax: vendorData.tax || "",
      taxNo: vendorData.tax_no || "",
      invoicePrefix: vendorData.invoice_prefix || "",
      website_link: vendorData.website_link || "",
      notes: vendorData.notes || "",
      email: vendorData.email || ""
    });
    const shopImages = toImageList(vendorData.office_images);
    setOfficeImages(shopImages);
    setInitialOfficeImages(shopImages);
    setNewImage(null);
    setProfileImage(null);
    setRemoveLogo(false);
    setRemoveCollab(false);
    setIsLoading(false);
  }, [show, vendorData]);

  const handleProfileImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Image size should be less than 200KB");
      return;
    }
    setRemoveLogo(false);
    const reader = new FileReader();
    reader.onloadend = () => setProfileImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Image size should be less than 200KB");
      return;
    }
    setRemoveCollab(false);
    const reader = new FileReader();
    reader.onloadend = () => setNewImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleOfficeImagesUpload = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    if (officeImages.length + files.length > MAX_OFFICE_IMAGES) {
      toast.error(`You can upload up to ${MAX_OFFICE_IMAGES} shop images`);
      return;
    }
    if (files.some((file) => file.size > MAX_IMAGE_SIZE)) {
      toast.error("Each shop image should be less than 200KB");
      return;
    }
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setOfficeImages((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeOfficeImage = (index) => {
    setOfficeImages((prev) => prev.filter((_, i) => i !== index));
  };

  const formatUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `https://${url}`;
  };

  const handleSubmit = async (values) => {
    const officeImagesChanged =
      officeImages.length !== initialOfficeImages.length ||
      officeImages.some((img, i) => img !== initialOfficeImages[i]);
    const hasImageChange =
      !!profileImage || removeLogo || !!newImage || removeCollab || officeImagesChanged;
    const hasFieldChange = Object.keys(formData).some(
      (key) => String(values[key] ?? "") !== String(formData[key] ?? "")
    );

    if (!hasFieldChange && !hasImageChange) {
      toast.info("No changes detected");
      return;
    }

    const payload = {
      website_link: values.website_link,
      notes: values.notes,
      tax: values.tax,
      taxNo: values.taxNo,
      invoicePrefix: values.invoicePrefix
    };

    if (values.email !== formData.email) {
      payload.email = values.email;
    }

    if (profileImage) {
      payload.profile_image = profileImage;
    } else if (removeLogo) {
      payload.profile_image = "";
    }

    if (newImage) {
      payload.image = newImage;
    } else if (removeCollab) {
      payload.image = "";
    }

    if (officeImagesChanged) {
      payload.office_images = officeImages;
    }

    try {
      const response = await apicall.post(payload, "ezpUpdateUser");
      if (response.status === 200) {
        toast.success("Business information updated successfully", {
          autoClose: 1500
        });
        onHide();
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast.error(
          response?.message || "Failed to update business information. Please try again."
        );
      }
    } catch (error) {
      toast.error("Failed to update business information. Please try again.");
    }
  };

  const SidebarSkeleton = () => (
    <div className="p-4">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="mb-3">
          <Skeleton width="100px" height="20px" marginBottom="8px" />
          <Skeleton width="100%" height="38px" />
        </div>
      ))}
    </div>
  );

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: "100%", maxWidth: "500px" }}
    >
      <Offcanvas.Header closeButton className="custom-border-bottom p-4">
        <Offcanvas.Title className="page-heading">
          Business Information
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="p-0 d-flex flex-column">
        {isLoading ? (
          <SidebarSkeleton />
        ) : (
          <div className="flex-grow-1 overflow-auto p-4">
            <Formik
              initialValues={formData}
              validationSchema={businessSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue
              }) => (
                <Form
                  onSubmit={handleSubmit}
                  className="d-flex flex-column h-100"
                  style={{ height: "100%" }}
                >
                  <div style={{ flex: 1, paddingBottom: "65px" }}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tax(%)</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="text"
                          name="tax"
                          value={values.tax}
                          onChange={handleChange}
                          className="form-input"
                        />
                        <span className="percent-symbol">%</span>
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Tax No (TRN No. for Dubai / GST No. for India)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="taxNo"
                        value={values.taxNo}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Invoice Number Prefix</Form.Label>
                      <Form.Control
                        type="text"
                        name="invoicePrefix"
                        value={values.invoicePrefix}
                        onChange={handleChange}
                        maxLength={10}
                        placeholder="INV"
                        className="form-input"
                        isInvalid={!!errors.invoicePrefix}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.invoicePrefix}
                      </Form.Control.Feedback>
                      <Form.Text muted>
                        Next invoices read{" "}
                        {(values.invoicePrefix || "INV").toUpperCase()}-
                        {new Date().getFullYear()}-000001. Leave blank for INV.
                        Existing invoices keep their numbers.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-1">
                      <Form.Label>Upload Logo</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleProfileImageUpload}
                        id="logoUpload"
                        style={{ display: "none" }}
                      />
                      <div className="mt-1">
                        <button
                          type="button"
                          className="btn-custom-danger"
                          onClick={() =>
                            document.getElementById("logoUpload").click()
                          }
                        >
                          <MdAttachFile /> Attach Logo
                        </button>
                      </div>
                      {profileImage ? (
                        <div className="mt-2 position-relative" style={{ width: "80px" }}>
                          <img src={profileImage} className="img-preview" alt="Logo" />
                          <span className="remove-btn" onClick={() => setProfileImage(null)}>
                            ×
                          </span>
                        </div>
                      ) : (
                        vendorData?.profile_image &&
                        !removeLogo && (
                          <div className="mt-2 position-relative" style={{ width: "80px" }}>
                            <img
                              src={vendorData.profile_image}
                              className="img-preview"
                              alt="Logo"
                            />
                            <span
                              className="remove-btn"
                              onClick={() => {
                                setRemoveLogo(true);
                                setProfileImage(null);
                              }}
                            >
                              ×
                            </span>
                          </div>
                        )
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Website Link</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="text"
                          name="website_link"
                          value={values.website_link}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="form-input pe-5"
                        />
                        {values.website_link && (
                          <span
                            className="open-link-icon"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              window.open(formatUrl(values.website_link), "_blank");
                            }}
                          >
                            <FaLink />
                          </span>
                        )}
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && errors.email}
                        className="form-input"
                      />
                      {touched.email && errors.email && (
                        <div className="invalid-feedback d-block">{errors.email}</div>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        type="text"
                        name="notes"
                        value={values.notes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-input"
                      />
                    </Form.Group>

                    <Form.Group className="mb-1">
                      <Form.Label>Upload Collaboration</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        id="CollaborationUpload"
                        style={{ display: "none" }}
                      />
                      <div className="mt-1">
                        <button
                          type="button"
                          className="btn-custom-danger"
                          onClick={() =>
                            document.getElementById("CollaborationUpload").click()
                          }
                        >
                          <MdAttachFile /> Attach Collaboration
                        </button>
                      </div>
                      {newImage ? (
                        <div className="mt-2 position-relative" style={{ width: "80px" }}>
                          <img src={newImage} className="img-preview" alt="Collaboration" />
                          <span className="remove-btn" onClick={() => setNewImage(null)}>
                            ×
                          </span>
                        </div>
                      ) : (
                        vendorData?.image &&
                        !removeCollab && (
                          <div className="mt-2 position-relative" style={{ width: "80px" }}>
                            <img
                              src={vendorData.image}
                              className="img-preview"
                              alt="Collaboration"
                            />
                            <span
                              className="remove-btn"
                              onClick={() => {
                                setRemoveCollab(true);
                                setNewImage(null);
                              }}
                            >
                              ×
                            </span>
                          </div>
                        )
                      )}
                    </Form.Group>

                    <Form.Group className="mb-1 mt-3">
                      <Form.Label>Shop Images</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        multiple
                        onChange={handleOfficeImagesUpload}
                        id="officeImagesUpload"
                        style={{ display: "none" }}
                      />
                      <div className="mt-1">
                        <button
                          type="button"
                          className="btn-custom-danger"
                          onClick={() =>
                            document.getElementById("officeImagesUpload").click()
                          }
                        >
                          <MdAttachFile /> Attach Shop Images
                        </button>
                      </div>
                      <div className="label mt-1">
                        Up to {MAX_OFFICE_IMAGES} images, each under 200KB
                      </div>
                      {officeImages.length > 0 && (
                        <div className="d-flex flex-wrap gap-3 mt-2">
                          {officeImages.map((img, index) => (
                            <div
                              key={`${img.slice(-24)}-${index}`}
                              className="position-relative"
                              style={{ width: "80px" }}
                            >
                              <img
                                src={img}
                                alt={`Shop ${index + 1}`}
                                className="img-preview"
                              />
                              <span
                                className="remove-btn"
                                onClick={() => removeOfficeImage(index)}
                              >
                                ×
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Form.Group>
                  </div>

                  <div className="bg-white custom-border-top p-3 d-flex gap-3">
                    <Button
                      variant="light"
                      onClick={onHide}
                      className="custom-radius btn-outline-borders"
                      type="button"
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-custom-danger custom-radius"
                      type="submit"
                      style={{ flex: 1 }}
                    >
                      Save Changes
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default BusinessInfoSidebar;
